import { filterRulesMapped } from '@/tools/filterRules'
import {
    dateSortFunction,
    getChildrenFromList,
    arrayToTextFormatted,
} from '@/tools/functions'
import SelectorComponent from '@/components/commons/selector/SelectorComponent.vue'
import { commandService } from '@/services/command.service'
import { thingService } from '@/services/thing.service'
import { configurationService } from '@/services/configuration.service'
import i18n from '@/i18n'
import { SelectorModel } from '@/constants/constants'
import { clone } from 'ramda'
import Vue from 'vue'
import { Util } from '@colven/common-domain-lib/lib'

// id del componente para guardar la configuración
const COMPONENT_ID = 'COMMAND_MASSIVE_UPLOAD'
// intervalo de actualización de los datos de la tabla
const UPDATE_INTERVAL = 1

export default {
    name: 'CommandMassiveUploadComponent',
    components: {
        SelectorComponent
    },
    data: () => ({
        // datos para la tabla
        tableHeaders: [],
        tableFilterRules: {},
        tableData: [],
        tableDataKey: '_id',
        tableComponentButtons: [],
        loadingTable: false,
        rowActions: [],
        disableRefreshTable: true,
        tableSettingBtnDisabled: false,
        tableConfig: {},
        // para controlar el navigation drawer
        showSelector: false,
        // modelo del selector
        selectorModel: clone(SelectorModel),
        // backup de datos del selector
        things: [],
        file: null,
        commands: [],
        forwardingParametersEnabled: false,
        forwardingInterval: 15,
        forwardingMaxAttemptTime: 1,
        // interval de actualización de los datos
        intervalId: null
    }),
    computed: {
        /**
         * Detalles del reporte (html)
         */
        details () {
            // nombres de equipos formateados
            const thingNames = this.things.map(thing => thing.name)
            const thingsTextFormatted = arrayToTextFormatted(thingNames)

            let details = `<h3>${this.$t('commandMassiveUpload.name')}</h3>`
            details += `<h5>${this.$t('equipment.name')}: ${thingsTextFormatted}</h5>`

            return details
        }
    },
    created () {
        // título
        this.selectorModel.title = i18n.t('commandMassiveUpload.name')

        // label del botón
        this.selectorModel.saveButtonText = i18n.t('commandMassiveUpload.sendCommands')

        // oculto el selector de rangos de tiempo
        this.selectorModel.showDateAndTimeSelector = false

        // selector de cosas (equipos)
        this.selectorModel.filters.push({
            id: 'things',
            name: i18n.t('equipment.name'),
            show: true,
            disabled: false,
            showDialog: false,
            singleSelect: false,
            data: [],
            selectedData: [],
            selectAction: undefined
        })

        // file input
        this.selectorModel.customInputComponent = {
            name: i18n.t('commandMassiveUpload.commands'),
            content: {
                _uid: '9d1zXJn184',
                component: 'CommandsFileInputComponent',
                headline: 'CommandsFileInputComponent'
            },
            model: {
                file: null,
                data: [],
                consoleInputCommands: null,
                show: true,
                showForwardingConfiguration: false,
                forwardingParametersEnabled: false,
                forwardingInterval: 15,
                forwardingMaxAttemptTime: 1
            }
        }
        this.selectorModel.showCustomInputComponent = true
    },
    beforeDestroy() {
        clearInterval(this.intervalId)
        this.intervalId = null
    },
    async mounted () {
        // cosas
        const things = await thingService.getAllForSelector()
        this.selectorModel.filters[0].data.cleanAndUpdate(things)
    
        // configuración (cosas seleccionadas en cargas anteriores)
        await this.getConfiguration()
    
        this.showSelector = true
    },
    methods: {
        /**
         * Acción de actualizar datos de la tabla
         */
        refreshTable () {
            this.loadingTable = true
            this.disableRefreshTable = true

            // ids de comandos (números)
            const commandIds = this.tableData.map(element => element._id)

            // obtengo los datos
            commandService.getMassiveUploadData(commandIds)
                .then(response => {
                    this.tableData.cleanAndUpdate(response.data)
                    this.loadingTable = false
                    this.disableRefreshTable = false
                })             
        },
        /**
         * Obtener los datos del reporte luego de procesar los datos de los filtros
         * @param {*} eventData 
         */
        async save (eventData) {
            this.showSelector = false
            this.loadingTable = true
            this.disableRefreshTable = true

            // cosas seleccionadas
            this.things = []
            getChildrenFromList(eventData.filters[0].selectedData, this.things)

            // backup comandos
            this.commands = eventData.customInputComponentModel.data
            this.forwardingParametersEnabled = eventData.customInputComponentModel.forwardingParametersEnabled
            this.forwardingInterval = eventData.customInputComponentModel.forwardingInterval
            this.forwardingMaxAttemptTime = eventData.customInputComponentModel.forwardingMaxAttemptTime

            // obtengo los datos
            const commandsResponse = await commandService.sendCommandsMassiveUpload(this.things, this.commands,
                this.forwardingParametersEnabled ? this.forwardingInterval : null,
                this.forwardingParametersEnabled ? Util.roundDecimals(this.forwardingMaxAttemptTime * 60) : null)
            this.tableData.cleanAndUpdate(commandsResponse.data)
            this.tableHeaders.cleanAndUpdate(commandsResponse.headers)
            this.setSortFunctions()
            this.setTableFilterRules()
            this.loadingTable = false
            this.disableRefreshTable = false    
            if (this.tableData.length > 0) {
                clearInterval(this.intervalId)
                this.intervalId = null
                this.updateData()
            }
        },
        /**
         * Para actualizar los datos de la tabla
         */
        updateData() {
            const that = this
            this.intervalId = setInterval(
                function () {
                    that.refreshTable()
                }, UPDATE_INTERVAL * 60 * 1000)
        },
        /**
         * Para mostrar/ocultar el selector
         */
        selector () {
            this.showSelector = !this.showSelector
        },
        /**
         * Para setear las reglas de filtrado
         */
        setTableFilterRules () {
            this.tableFilterRules = {}
            this.tableHeaders.forEach(header => {
                this.tableFilterRules[header.value] = filterRulesMapped[header.filterType]
            })
        },
        /**
         * Para setear las funciones de ordenamiento
         */
        setSortFunctions () {
            /*
                Para ordenar las columnas fecha y hora
                TO DO: analizar la posibilidad de incluir este tipo de sorting en el componente genérico
            */
            this.tableHeaders.find(header => header.value === 'createDate').sort = (a, b) => { return dateSortFunction(a, b, 'DD/MM/YYYY HH:mm:ss') }
            this.tableHeaders.find(header => header.value === 'lastSendDate').sort = (a, b) => { return dateSortFunction(a, b, 'DD/MM/YYYY HH:mm:ss') }
        },
        /**
         * Manejador de acciones de los botones de las filas
         * @param {*} button 
         * @param {*} data 
         */
        // eslint-disable-next-line no-unused-vars
        rowButtonClicked (button, data) {
            switch (button.id) {
                default:
                    console.warn('Button action not implemented')
                    break
            }
        },
        /**
         * Para validar el selector de filtros y habilitar/deshabilitar el botón para guardar
         */
        validateSelector () {
            const that = this
            Vue.nextTick(function () {
                const thingsSelected = that.selectorModel.filters[0].selectedData
                const commandsUploaded = that.selectorModel.customInputComponent.model.data
                that.selectorModel.disableSaveButton =
                    thingsSelected.length === 0 || commandsUploaded.length === 0
            })
        },
        /**
         * Para obtener la configuración de filtros
         * Esta función se ejecuta en mounted
         */
        async getConfiguration () {
            const config = await configurationService.get(COMPONENT_ID)

            if (config && config.data) {
                // config de la tabla
                if (config.data.tableConfig) {
                    this.tableConfig = config.data.tableConfig
                } else {
                    this.tableConfig = {}
                }
            }
        },
        /**
         * Acción guardar configuración del botón de la tabla
         * @param {*} tableConfig
         */
        saveConfig (tableConfig) {
            // guardo la configuración
            this.tableConfig = tableConfig;
            configurationService.save(COMPONENT_ID, {
                things: this.things,
                tableConfig
            }, true)
        }
    },
    watch: {
        'selectorModel.filters': {
            handler: function () {
                this.validateSelector()
            },
            deep: true
        },
        'selectorModel.customInputComponent.model': {
            handler: function () {
                this.validateSelector()
            },
            deep: true
        },
    }
}
